import {
	forwardRef,
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import {
	AttachmentList,
	Portal,
	BusyIndicator,
	Icon
} from "client/Components";
import { getExtensionAndType } from "client_legacy/Documents/fileExtensions";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { localize } from "client_legacy/ndaw/culture";
import { useViewAttachmentHandler } from "./Actions/useViewAttachementHandler";
import { useSaveAttachmentHandler } from "./Actions/useSaveAttachmentHandler";
import { useSaveAttachmentAsHandler } from "./Actions/useSaveAttachmentAsHandler";
import { useDownloadAttachmentHandler } from "./Actions/useDownloadAttachmentHandler";
import { generateTrackingId, isNil } from "client/Misc/utils";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { AttachmentModel } from "client/Api/Email/email";
import { MailMessageLocationModel } from "client/Api/Email/common";
import { useIsTouchDevice } from "client/Hooks";
import { GridItemContentHandle } from "client/Components/GridItem/GridItemContent";

export type AttachmentsProps = {
	attachments: readonly AttachmentModel[];
	messageLocation: MailMessageLocationModel;
};

export type AttachmentsHandle = {
	closeDropOvers(): void;
};

export const Attachments = forwardRef(
	(props: AttachmentsProps, ref: Ref<AttachmentsHandle>): ReactElement => {
		const {
			attachments,
			messageLocation
		} = props;

		const attachmentRefs: ReadonlyRefObject<Map<string, GridItemContentHandle>> = useRef<Map<string, GridItemContentHandle>>(new Map());

		const setAttachmentRef = (attachmentId: string, ref: GridItemContentHandle | nil): void => {
			if (isNil(ref)) {
				attachmentRefs.current
					.delete(attachmentId);
			} else {
				attachmentRefs.current
					.set(attachmentId, ref);
			}
		};

		const isSharepointConfigured: boolean = useMemo(
			(): boolean => {
				return documentsConfiguration().sharePoint
					.isConfigured;
			},
			[]
		);

		useImperativeHandle(
			ref,
			(): AttachmentsHandle => ({
				closeDropOvers: (): void => {
					attachmentRefs.current
						.forEach(h => h.closeActions());
				}
			}),
			[]
		);

		return (
			<AttachmentList
				trackingId="email-attachments"
			>
				{attachments.map(
					a => (
						<Attachment
							key={a.exchangeAttachmentId}
							model={a}
							messageLocation={messageLocation}
							isSharepointConfigured={isSharepointConfigured}
							ref={x => setAttachmentRef(a.exchangeAttachmentId, x)}
						/>
					)
				)}
			</AttachmentList>
		);
	}
);
Attachments.displayName = "Attachments";

export type AttachmentProps = {
	model: AttachmentModel;
	messageLocation: MailMessageLocationModel;
	isSharepointConfigured: boolean;
};

export const Attachment = forwardRef(
	(props: AttachmentProps, ref: Ref<GridItemContentHandle>): ReactElement => {
		const {
			model,
			messageLocation,
			isSharepointConfigured
		} = props;

		const {
			name,
			exchangeAttachmentId
		} = model;

		const [isBusy, setIsBusy] = useState<boolean>(false);
		const { type } = getExtensionAndType(name);

		const isTouch: boolean = useIsTouchDevice();

		const viewAttachmentHandler: (() => void) = useViewAttachmentHandler(
			{
				attachment: model,
				messageLocation,
				onIsBusyChange: setIsBusy
			}
		);

		const saveAttachmentHandler: (() => void) = useSaveAttachmentHandler({
			attachment: model,
			messageLocation,
			onIsBusyChange: setIsBusy
		});

		const {
			saveAttachmentAsHandler,
			saveAttachmentDialog
		} = useSaveAttachmentAsHandler({
			attachment: model,
			messageLocation
		});

		const downloadAttachmentHandler: (() => void) = useDownloadAttachmentHandler({
			attachment: model,
			messageLocation
		});

		const actions = useMemo(
			(): readonly Action<ActionContext>[] => [
				{
					kind: ActionKind.Regular,
					name: "view",
					title: localize("_View"),
					canExecute: () => isSharepointConfigured,
					execute: (): void => viewAttachmentHandler(),
					icon: "eye"
				},
				{
					kind: ActionKind.Regular,
					name: "save",
					title: localize("_Save"),
					canExecute: () => isSharepointConfigured,
					execute: (): void => saveAttachmentHandler(),
					icon: "floppy-disk"
				},
				{
					kind: ActionKind.Regular,
					name: "save-as",
					title: localize("_SaveAs"),
					canExecute: (): boolean => true,
					execute: (): void => saveAttachmentAsHandler(),
					icon: "floppy-disk"
				},
				{
					kind: ActionKind.Regular,
					name: "download",
					title: localize("_Download"),
					canExecute: (): boolean => true,
					execute: (): void => downloadAttachmentHandler(),
					icon: "download"
				},
			],
			[
				downloadAttachmentHandler,
				isSharepointConfigured,
				saveAttachmentAsHandler,
				saveAttachmentHandler,
				viewAttachmentHandler
			]
		);

		return (
			<>
				<AttachmentList.Item
					trackingId={generateTrackingId("attachment", `|${exchangeAttachmentId}`)}
					onClick={
						isSharepointConfigured
							? viewAttachmentHandler
							: saveAttachmentAsHandler
					}
					isTouch={isTouch}
					actions={actions}
					actionsTitle={name}
					actionContext={{isSharepointConfigured}}
				>
					<AttachmentList.Item.Content
						ref={ref}
						thumbnail={
							<Icon
								value={{
									kind: "svg",
									name: type
								}}
							/>
						}
						name={name}
						actions={actions}
						actionContext={{ isSharepointConfigured }}
					/>
				</AttachmentList.Item>
				{isBusy && (
					<Portal
						container={document.body}
					>
						<BusyIndicator />
					</Portal>
				)}

				{saveAttachmentDialog}
			</>
		);
	}
);
Attachment.displayName = "Attachment";

type ActionContext = {
	isSharepointConfigured: boolean;
};